import { EventTrackingAction, UserEvent } from '../declarations';
import { ContextualData, emptyContextualData, JourneyEventContextualData } from '../contextualData';
import { globalObservability } from '../global';
import { BaseJourneyEventWrapper, JourneyEventHelpers } from '../declarations/JourneyEvent';
import { EventWrapper } from '../__generated__/protobuf/journey/data/event_wrapper';

export const buildDefaultDatadogOptions = (opts: {
  appName: string;
  env: string;
  version: string;
}) => {
  const { appName, env, version } = opts;
  return {
    site: 'datadoghq.com',
    version,
    env,
    service: appName,
  };
};

export function getContextualDataFromDatadog(): ContextualData {
  const sessionId = globalObservability.getDatadogRumClient().getInternalContext?.()?.session_id;
  if (sessionId) {
    return {
      data: {
        'datadog-rum-session-id': sessionId,
        'datadog-rum-session-replay': `https://app.datadoghq.com/rum/replay/sessions/${sessionId}`,
        'datadog-rum-session-search': `https://app.datadoghq.com/rum/explorer?query=%40session.id%3A${sessionId}`,
      },
    };
  } else {
    return emptyContextualData;
  }
}

export function trackPerformanceInDatadog(eventName: string) {
  globalObservability.getDatadogRumClient().addTiming?.(eventName);
}

export function addRUMGlobalContext(context: { [k: string]: unknown }) {
  globalObservability.getDatadogRumClient()?.setRumGlobalContext?.({
    ...globalObservability.getDatadogRumClient().getRumGlobalContext?.(),
    ...context,
  });
}

export function trackEventInDatadog<AppActions>(
  action: EventTrackingAction<AppActions>,
  label?: string,
  contextualData: ContextualData = emptyContextualData,
) {
  globalObservability.getDatadogRumClient().addAction?.(action, {
    ...contextualData.data,
    adClickContext: contextualData.adClickContext,
    campaignContext: contextualData.campaignContext,
    customerContext: contextualData.customerContext,
    exclusiveOffersContext: contextualData.exclusiveOffersContext,
    leadContext: contextualData.leadContext,
    sellDirectContext: contextualData.sellDirectContext,
    label,
  });
}

export function trackUserEventInDatadog(
  event: UserEvent,
  contextualData: ContextualData = emptyContextualData,
) {
  globalObservability
    .getDatadogRumClient()
    .addAction?.(Object.keys(event)?.[0] ?? 'unknownUserEvent', {
      ...event,
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      exclusiveOffersContext: contextualData.exclusiveOffersContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext,
    });
}

export function trackJourneyEventInDatadog<AppJourneyEventWrapper extends BaseJourneyEventWrapper>(
  event: Pick<AppJourneyEventWrapper, 'category'>,
  contextualData: JourneyEventContextualData,
  journeyEventHelpers: JourneyEventHelpers,
) {
  const category = (event.category as EventWrapper['category'])?.$case ?? 'unknownUserEvent';
  const journeyEventJSON = journeyEventHelpers.toJSON(event) as object;
  globalObservability.getDatadogRumClient().addAction?.(category, {
    ...journeyEventJSON,
    campaignContext: contextualData.campaignContext,
  });
}

export function trackErrorInDatadog(
  label: string,
  error?: Error,
  contextualData: ContextualData = emptyContextualData,
) {
  if (shouldExcludeErrorByMessage(error)) {
    return;
  }

  globalObservability.getDatadogRumClient().addError?.(error || new Error(label), {
    ...contextualData.data,
    label,
  });
}

// Array will perform better than Set when N is tiny by avoiding a hashing operation. Consider converting to a
// Set<string> if N ever becomes large, and after benchmarking performance.
const ERROR_MESSAGES_TO_EXCLUDE: string[] = [
  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  'ResizeObserver loop limit exceeded',
];

export const shouldExcludeErrorByMessage = (error?: Error): boolean => {
  return ERROR_MESSAGES_TO_EXCLUDE.includes(error?.message || '');
};
